/**
 * Socket service — lightweight wrapper
 *
 * On shared hosting, persistent WebSocket connections are unreliable
 * (proxies kill idle connections after 30-60 s). We therefore use
 * HTTP polling as the primary real-time strategy and only attempt a
 * WebSocket upgrade as an optional enhancement.
 *
 * The socket is:
 *  - connected ONLY when a logged-in user is present
 *  - disconnected immediately when the tab becomes hidden
 *  - reconnected (lazily) when the tab becomes visible again
 *  - NOT pinged at all — we rely on the server's order-update HTTP
 *    polling instead of a persistent socket for most real-time needs
 */

import { io } from 'socket.io-client'
import { API_URL } from 'config'

const SOCKET_URL = (API_URL || 'http://localhost:5000/api').replace(/\/api\/?$/, '')

let socket = null
let _customerId = null

const authenticateSocket = async () => {
  if (!socket) return

  try {
    const { auth } = await import('config/firebase')
    const token = auth.currentUser ? await auth.currentUser.getIdToken() : null
    if (token) {
      socket.emit('authenticate', { token })
    }
  } catch (error) {
    console.warn('Socket authentication skipped:', error)
  }
}

// ─── Visibility-aware connect / disconnect ────────────────────────────────────
const onVisibilityChange = () => {
  if (!_customerId) return
  if (document.visibilityState === 'hidden') {
    // Tab hidden → disconnect immediately to free server resources
    if (socket?.connected) socket.disconnect()
  } else {
    // Tab visible again → reconnect lazily
    if (socket && !socket.connected) socket.connect()
  }
}

export const getSocket = () => socket

export const connectSocket = (customerId) => {
  if (!customerId) return null
  _customerId = customerId

  if (!socket) {
    socket = io(SOCKET_URL, {
      autoConnect: false,
      // Prefer long-polling first — works on every shared host.
      // Socket.IO will upgrade to WebSocket automatically if supported.
      transports: ['polling', 'websocket'],
      withCredentials: true,
      // No engine-level pings — we rely on HTTP polling for liveness
      upgrade: true,
      rememberUpgrade: false,
      // Reconnect but with a long delay to avoid hammering the server
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 10000,      // 10 s before first retry
      reconnectionDelayMax: 60000,   // cap at 60 s
    })

    socket.on('connect', () => {
      authenticateSocket()
    })

    // Register visibility listener once
    document.addEventListener('visibilitychange', onVisibilityChange)
  }

  // Only connect if tab is currently visible
  if (document.visibilityState !== 'hidden' && !socket.connected) {
    socket.connect()
  }

  authenticateSocket()
  return socket
}

export const disconnectSocket = () => {
  _customerId = null
  document.removeEventListener('visibilitychange', onVisibilityChange)
  if (socket) {
    socket.disconnect()
    socket = null
  }
}
